import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { FileText } from "lucide-react";
import DateInput from "@/components/DateInput";

const EMPTY = { name: "", numero_contrato: "", fecha_inicio: "", fecha_termino: "", dotacion: "", gerencia: "" };

/**
 * Diálogo para crear o editar un contrato de un mandante.
 * Props:
 *  - open: boolean
 *  - onOpenChange: (bool) => void
 *  - initial: contrato | null  (si es null => modo creación)
 *  - onSubmit: async (payload) => void  (resuelve si guardado OK, lanza para errores)
 *  - mandanteName: string (para el título)
 */
export default function ContratoFormDialog({ open, onOpenChange, initial, onSubmit, mandanteName }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const isEdit = !!initial?.id;

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setForm({
        name: initial.name || "",
        numero_contrato: initial.numero_contrato || "",
        fecha_inicio: initial.fecha_inicio || "",
        fecha_termino: initial.fecha_termino || "",
        dotacion: initial.dotacion ?? "",
        gerencia: initial.gerencia || "",
      });
    } else {
      setForm(EMPTY);
    }
    setError("");
  }, [open, initial]);

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async () => {
    setError("");
    if (!form.name.trim()) { setError("La Glosa es obligatoria."); return; }
    if (!form.numero_contrato.trim()) { setError("El Número de Contrato es obligatorio."); return; }
    if (form.dotacion !== "" && (isNaN(Number(form.dotacion)) || Number(form.dotacion) < 0)) {
      setError("La Dotación debe ser un número ≥ 0."); return;
    }
    setSaving(true);
    try {
      await onSubmit({
        name: form.name.trim(),
        numero_contrato: form.numero_contrato.trim(),
        fecha_inicio: form.fecha_inicio || null,
        fecha_termino: form.fecha_termino || null,
        dotacion: form.dotacion === "" ? null : Number(form.dotacion),
        gerencia: form.gerencia.trim(),
      });
      onOpenChange(false);
    } catch (e) {
      setError(e?.response?.data?.detail || "No se pudo guardar");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <FileText size={18} className="text-indigo-600" />
            {isEdit ? "Editar contrato" : "Nuevo contrato"}
            {mandanteName && <span className="text-xs font-normal text-slate-500">· {mandanteName}</span>}
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
          <Field label="Número de Contrato *" testid="cf-numero">
            <input value={form.numero_contrato} onChange={set("numero_contrato")} placeholder="Ej: VCA-2024-001"
              className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-indigo-400 outline-none text-sm" />
          </Field>
          <Field label="Glosa *" testid="cf-glosa">
            <input value={form.name} onChange={set("name")} placeholder="Ej: Proyecto VCA"
              className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-indigo-400 outline-none text-sm" />
          </Field>
          <Field label="Fecha de Inicio" testid="cf-inicio">
            <DateInput value={form.fecha_inicio}
              onChange={(iso) => setForm((s) => ({ ...s, fecha_inicio: iso }))} />
          </Field>
          <Field label="Fecha de Término" testid="cf-termino">
            <DateInput value={form.fecha_termino}
              onChange={(iso) => setForm((s) => ({ ...s, fecha_termino: iso }))} />
          </Field>
          <Field label="Dotación" testid="cf-dotacion">
            <input type="number" min="0" value={form.dotacion} onChange={set("dotacion")} placeholder="N° trabajadores"
              className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-indigo-400 outline-none text-sm" />
          </Field>
          <Field label="Gerencia" testid="cf-gerencia">
            <input value={form.gerencia} onChange={set("gerencia")} placeholder="Ej: Operaciones"
              className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-indigo-400 outline-none text-sm" />
          </Field>
        </div>
        {error && <p data-testid="cf-error" className="text-xs text-red-600 pt-1">{error}</p>}
        <DialogFooter className="pt-3">
          <button data-testid="cf-cancel" onClick={() => onOpenChange(false)} disabled={saving}
            className="text-sm text-slate-600 hover:text-slate-900 px-4 py-2 rounded-lg">Cancelar</button>
          <button data-testid="cf-submit" onClick={submit} disabled={saving}
            className="inline-flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-sm font-semibold px-4 py-2 rounded-lg">
            {saving ? "Guardando…" : (isEdit ? "Guardar cambios" : "Crear contrato")}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, testid, children }) {
  return (
    <label data-testid={testid} className="block">
      <span className="text-[11px] uppercase tracking-wide text-slate-500 block mb-1">{label}</span>
      {children}
    </label>
  );
}
